/**
 * GFG-271 — Reports: Bounces. Pure helpers: the Blotter query state <-> API params translation,
 * the Action-cell decision, and the Fix modal's email validation.
 */
import z from "zod";
import type { AppliedFilter } from "@/components/BlotterFilterBar";
import type { BounceSortKey } from "@/constants/reportsBounces";
import {
  BOUNCE_STATUSES,
  BOUNCES_TEXT,
  COLUMN_TO_SORT_KEY,
  FILTER_FIELD_IDS,
  MAX_EMAIL_LENGTH,
} from "@/constants/reportsBounces";
import type {
  BounceAction,
  BounceColumnId,
  BounceRow,
  BouncesListParams,
} from "@/interfaces/reportsBounces";

/** Blotter query state -> API list params (chips -> campaign_id/recipient_name/recipient_email). */
export function toBouncesListParams(q: {
  search: string;
  filters: AppliedFilter[];
  page: number;
  limit: number;
}): BouncesListParams {
  const params: BouncesListParams = {
    search: q.search.trim() || undefined,
    page: q.page,
    limit: q.limit,
  };

  for (const filter of q.filters) {
    switch (filter.fieldId) {
      case FILTER_FIELD_IDS.campaign:
        if (filter.values.length) params.campaign_id = filter.values;
        break;
      case FILTER_FIELD_IDS.recipientName:
        if (filter.values[0]) params.recipient_name = filter.values[0];
        break;
      case FILTER_FIELD_IDS.recipientEmail:
        if (filter.values[0]) params.recipient_email = filter.values[0];
        break;
      default:
        break;
    }
  }

  return params;
}

/** Column key -> sort key, discarding any key outside the whitelist. */
export function toBouncesSortBy(columnKey: string): BounceSortKey | undefined {
  return COLUMN_TO_SORT_KEY[columnKey as BounceColumnId] ?? undefined;
}

/** The row's status, showing Resent right away for a row resent in this session, before the refetch. */
export function bounceStatus(row: Pick<BounceRow, "status">, resentLocally: boolean) {
  return resentLocally && row.status === BOUNCE_STATUSES.UNRESOLVED
    ? BOUNCE_STATUSES.RESENT
    : row.status;
}

/** Only an unresolved bounce has an action: ended (!row.campaignLive) > fix (ctx.canSend) > none */
export function bounceAction(
  row: Pick<BounceRow, "campaignLive" | "status">,
  ctx: { canSend: boolean; resentLocally: boolean },
): BounceAction {
  if (bounceStatus(row, ctx.resentLocally) !== BOUNCE_STATUSES.UNRESOLVED) return "none";
  if (!row.campaignLive) return "ended";
  if (ctx.canSend) return "fix";
  return "none";
}

/**
 * null = no message. "" -> null (button simply disabled). Over MAX_EMAIL_LENGTH -> tooLong.
 * Invalid -> invalidEmail. Same as bounced (trim + lowercase) -> sameEmail.
 */
export function newEmailError(candidate: string, bouncedEmail: string | null): string | null {
  const trimmed = candidate.trim();
  if (trimmed === "") return null;
  // Same checks as the API's ResendBounceBody, so the modal never accepts an address the server rejects.
  if (trimmed.length > MAX_EMAIL_LENGTH) return BOUNCES_TEXT.modal.tooLong;
  if (!z.email().safeParse(trimmed).success) return BOUNCES_TEXT.modal.invalidEmail;
  if (bouncedEmail !== null && trimmed.toLowerCase() === bouncedEmail.trim().toLowerCase()) {
    return BOUNCES_TEXT.modal.sameEmail;
  }
  return null;
}

/** trimmed non-empty && newEmailError() === null */
export function isValidNewEmail(candidate: string, bouncedEmail: string | null): boolean {
  return candidate.trim() !== "" && newEmailError(candidate, bouncedEmail) === null;
}

/** `${recipientName}${BOUNCES_TEXT.modal.subtitleSeparator}${campaignName}` */
export function bounceSubtitle(row: Pick<BounceRow, "recipientName" | "campaignName">): string {
  return `${row.recipientName}${BOUNCES_TEXT.modal.subtitleSeparator}${row.campaignName}`;
}
